/**
 * Build Your Own Bundle (BYOB)
 * Custom bundle builder. Adds selected variants to cart in one request;
 * the actual tiered discount is applied automatically in the cart by DollarLabs.
 * `_byob:1` is added only to full-price builder lines. DollarLabs therefore
 * counts and discounts exactly the same items. Sale lines remain purchasable in
 * the builder but receive no marker; once their compare-at sale ends, they
 * automatically become normal BYOB lines again. The tier config here only
 * drives the on-page progress/savings UI and MUST mirror DollarLabs.
 */
if (!customElements.get('byob-builder')) {
  customElements.define(
    'byob-builder',
    class ByobBuilder extends HTMLElement {
      constructor() {
        super();

        this.tier1Qty = parseInt(this.dataset.tier1Qty, 10) || 3;
        this.tier1Pct = parseFloat(this.dataset.tier1Pct) || 0;
        this.tier2Qty = parseInt(this.dataset.tier2Qty, 10) || 4;
        this.tier2Pct = parseFloat(this.dataset.tier2Pct) || 0;
        this.tier2AllCats = this.dataset.tier2AllCategories === 'true';
        this.categoryCount = parseInt(this.dataset.categoryCount, 10) || 0;
        this.strings = {
          firstItem: this.dataset.stringFirstItem,
          maxDiscount: this.dataset.stringMaxDiscount,
          onlyRemaining: this.dataset.stringOnlyRemaining,
          remaining: this.dataset.stringRemaining,
          initialNudge: this.dataset.stringInitialNudge,
          maxDiscountNudge: this.dataset.stringMaxDiscountNudge,
          activeNudge: this.dataset.stringActiveNudge,
          remainingNudge: this.dataset.stringRemainingNudge,
          missingItemsCategories: this.dataset.stringMissingItemsCategories,
          missingCategories: this.dataset.stringMissingCategories,
          missingItems: this.dataset.stringMissingItems,
          bestDiscount: this.dataset.stringBestDiscount,
          discountActive: this.dataset.stringDiscountActive,
          noDiscount: this.dataset.stringNoDiscount,
          discountActiveHtml: this.dataset.stringDiscountActiveHtml,
          youSave: this.dataset.stringYouSave,
          addError: this.dataset.stringAddError,
          networkError: this.dataset.stringNetworkError,
        };
        this.cards = Array.from(this.querySelectorAll('.byob-card'));

        this.summary = this.querySelector('[data-byob-summary]');
        this.fillEl = this.querySelector('[data-byob-fill]');
        this.hintEl = this.querySelector('[data-byob-hint]');
        this.rateEl = this.querySelector('[data-byob-rate]');
        this.chips = Array.from(this.querySelectorAll('[data-chip]'));
        this.milestones = Array.from(this.querySelectorAll('[data-byob-milestone]'));
        this.countEl = this.querySelector('[data-byob-count]');
        this.tierEl = this.querySelector('[data-byob-tier]');
        this.priceEl = this.querySelector('[data-byob-price]');
        this.oldEl = this.querySelector('[data-byob-old]');
        this.savingsEl = this.querySelector('[data-byob-savings]');
        this.nudgeEl = this.querySelector('[data-byob-nudge]');
        this.addBtn = this.querySelector('[data-byob-add]');
        this.spinner = this.querySelector('[data-byob-spinner]');
        this.errorEl = this.querySelector('[data-byob-error]');

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        if (this.addBtn) this.addBtn.addEventListener('click', this.onAddToCart.bind(this));

        this.recalculate();
        this.refreshLiveProduct('kailar-twisted-glasfilter');
      }

      /**
       * Shopify can keep a page-cache snapshot after a product or manual
       * collection changes. Refresh the Glasfilter card from its uncached
       * product JSON so BYOB always exposes the current live variants.
       */
      async refreshLiveProduct(handle) {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const product = await response.json();
          if (!product || product.handle !== handle || !Array.isArray(product.variants)) {
            throw new Error('invalid product payload');
          }

          const card = this.findOrCreateLiveProductCard(product);
          if (!card) throw new Error('Zubehör category unavailable');

          this.hydrateLiveProductCard(card, product);
          this.cards = Array.from(this.querySelectorAll('.byob-card'));
          this.recalculate();
        } catch (error) {
          console.warn(`[BYOB] Could not refresh ${handle}; using server-rendered fallback.`, error);
        }
      }

      findOrCreateLiveProductCard(product) {
        const escapedHandle =
          window.CSS && typeof window.CSS.escape === 'function'
            ? window.CSS.escape(product.handle)
            : product.handle.replace(/[^a-z0-9_-]/gi, '');
        const existing =
          this.querySelector(`[data-product-handle="${escapedHandle}"]`) ||
          Array.from(this.querySelectorAll('.byob-card')).find((card) => {
            const href = card.querySelector('.byob-imgbox')?.getAttribute('href') || '';
            return href.split('?')[0].endsWith(`/products/${product.handle}`);
          });
        if (existing) {
          const grid = existing.closest('.byob-grid');
          if (existing.dataset.discountCategory === 'zubehor' && grid) {
            grid.prepend(existing);
          }
          return existing;
        }

        const accessoryCard = this.querySelector(
          '.byob-card[data-discount-category="zubehor"]'
        );
        const grid = accessoryCard?.closest('.byob-grid');
        if (!accessoryCard || !grid) return null;

        const priorCount = grid.querySelectorAll(':scope > .byob-card').length;
        const card = accessoryCard.cloneNode(true);
        card.classList.remove('byob-card--sel');
        card.dataset.productHandle = product.handle;
        card.querySelector('.byob-qty__n').textContent = '0';
        const badge = card.querySelector('[data-byob-card-badge]');
        if (badge) {
          badge.textContent = '0';
          badge.hidden = true;
        }
        grid.prepend(card);

        const cards = grid.querySelectorAll(':scope > .byob-card');
        if (cards.length > priorCount) cards[cards.length - 1].remove();
        return card;
      }

      hydrateLiveProductCard(card, product) {
        const availableVariants = product.variants.filter((variant) => variant.available);
        const firstVariant = availableVariants[0] || product.variants[0];
        if (!firstVariant) throw new Error('product has no variants');

        card.dataset.productId = String(product.id);
        card.dataset.productHandle = product.handle;
        card.classList.toggle('byob-card--soldout', availableVariants.length === 0);

        const link = card.querySelector('.byob-imgbox');
        if (link) link.href = `/products/${product.handle}`;
        const name = card.querySelector('.byob-name');
        if (name) name.textContent = product.title;

        const oldVariantId = this.readCardVariant(card).variantId;
        const variantControl = this.createVariantControl(
          product,
          availableVariants.length ? availableVariants : product.variants,
          oldVariantId
        );
        const controlWrap = card.querySelector('.byob-sizewrap');
        const hiddenControl = card.querySelector('.byob-variant');
        if (variantControl.tagName === 'SELECT') {
          if (controlWrap) {
            controlWrap.replaceChildren(variantControl);
          } else if (hiddenControl) {
            const wrap = document.createElement('div');
            wrap.className = 'byob-sizewrap';
            wrap.append(variantControl);
            hiddenControl.replaceWith(wrap);
          }
        } else if (hiddenControl) {
          hiddenControl.replaceWith(variantControl);
        } else if (controlWrap) {
          controlWrap.replaceWith(variantControl);
        }

        const selected = this.readCardVariant(card);
        const selectedVariant =
          product.variants.find((variant) => String(variant.id) === String(selected.variantId)) ||
          firstVariant;
        this.updateLiveProductImage(card, product, selectedVariant);

        const priceBox = card.querySelector('[data-byob-card-price]');
        if (priceBox) {
          priceBox.dataset.basePrice = String(selected.price);
          priceBox.dataset.compareAtPrice = String(selected.compareAtPrice);
        }
      }

      createVariantControl(product, variants, selectedVariantId) {
        if (variants.length === 1) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.className = 'byob-variant';
          input.value = String(variants[0].id);
          const price = this.priceToCents(variants[0].price);
          const compareAtPrice = this.priceToCents(variants[0].compare_at_price || 0);
          input.dataset.price = String(price);
          input.dataset.compareAtPrice = String(compareAtPrice);
          input.dataset.discountEligible = String(compareAtPrice <= price);
          return input;
        }

        const select = document.createElement('select');
        select.className = 'byob-size';
        select.setAttribute('aria-label', `Produktvariante: ${product.title}`);
        const preferredId = variants.some(
          (variant) => String(variant.id) === String(selectedVariantId)
        )
          ? String(selectedVariantId)
          : String(variants[0].id);

        variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          const price = this.priceToCents(variant.price);
          const compareAtPrice = this.priceToCents(variant.compare_at_price || 0);
          option.dataset.price = String(price);
          option.dataset.compareAtPrice = String(compareAtPrice);
          option.dataset.discountEligible = String(compareAtPrice <= price);
          option.textContent = `${variant.title} · ${this.formatMoney(
            price
          )}`;
          const image = variant.featured_image || product.featured_image;
          if (image) {
            const source = typeof image === 'string' ? image : image.src;
            option.dataset.image = this.resizeImageUrl(source, 320);
            option.dataset.imageSrcset = [
              `${this.resizeImageUrl(source, 160)} 160w`,
              `${this.resizeImageUrl(source, 320)} 320w`,
              `${this.resizeImageUrl(source, 480)} 480w`,
            ].join(', ');
            option.dataset.imageAlt =
              (typeof image === 'object' && image.alt) || product.title;
          }
          option.selected = option.value === preferredId;
          select.append(option);
        });
        return select;
      }

      updateLiveProductImage(card, product, variant) {
        const image = variant.featured_image || product.featured_image;
        const img = card.querySelector('.byob-imgbox img');
        if (!image || !img) return;

        const source = typeof image === 'string' ? image : image.src;
        img.src = this.resizeImageUrl(source, 320);
        img.srcset = [
          `${this.resizeImageUrl(source, 160)} 160w`,
          `${this.resizeImageUrl(source, 320)} 320w`,
          `${this.resizeImageUrl(source, 480)} 480w`,
        ].join(', ');
        img.alt = (typeof image === 'object' && image.alt) || product.title;
      }

      resizeImageUrl(source, width) {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      }

      priceToCents(price) {
        return typeof price === 'number' ? price : Math.round(Number(price) * 100);
      }

      onClick(evt) {
        const btn = evt.target.closest('.byob-qty button');
        if (!btn) return;
        const qtyBox = btn.closest('.byob-qty');
        const qtyEl = qtyBox.querySelector('.byob-qty__n');
        const min = parseInt(qtyBox.dataset.min, 10) || 0;
        const step = parseInt(btn.dataset.step, 10);
        let qty = (parseInt(qtyEl.textContent, 10) || 0) + step;
        if (qty < min) qty = min;
        qtyEl.textContent = qty;
        const card = qtyBox.closest('.byob-card');
        card.classList.toggle('byob-card--sel', qty > 0);
        const badge = card.querySelector('[data-byob-card-badge]');
        if (badge) {
          badge.textContent = qty;
          badge.hidden = qty === 0;
        }
        this.recalculate();
      }

      onChange(evt) {
        const select = evt.target.closest('.byob-size');
        if (!select) return;
        this.updateVariantMedia(select);
        this.recalculate();
      }

      updateVariantMedia(select) {
        const option = select.options[select.selectedIndex];
        if (!option) return;
        const card = select.closest('.byob-card');
        const img = card && card.querySelector('.byob-imgbox img');
        if (!img || !option.dataset.image) return;

        img.src = option.dataset.image;
        if (option.dataset.imageSrcset) img.srcset = option.dataset.imageSrcset;
        if (option.dataset.imageAlt) img.alt = option.dataset.imageAlt;
      }

      readCardVariant(card) {
        const variantEl = card.querySelector('.byob-size, .byob-variant');
        if (!variantEl) {
          return { variantId: null, price: 0, compareAtPrice: 0, discountEligible: true };
        }
        if (variantEl.tagName === 'SELECT') {
          const opt = variantEl.options[variantEl.selectedIndex];
          const price = parseInt(opt && opt.dataset.price, 10) || 0;
          return {
            variantId: opt && opt.value,
            price,
            compareAtPrice: parseInt(opt && opt.dataset.compareAtPrice, 10) || 0,
            discountEligible: !opt || opt.dataset.discountEligible !== 'false',
          };
        }
        const price = parseInt(variantEl.dataset.price, 10) || 0;
        return {
          variantId: variantEl.value,
          price,
          compareAtPrice: parseInt(variantEl.dataset.compareAtPrice, 10) || 0,
          discountEligible: variantEl.dataset.discountEligible !== 'false',
        };
      }

      readSelection() {
        const items = [];
        const categories = new Set();
        let selectedQty = 0;
        let totalQty = 0;
        let subtotal = 0;
        let discountableSubtotal = 0;
        let fixedPriceQty = 0;

        this.cards.forEach((card) => {
          const qty = parseInt(card.querySelector('.byob-qty__n').textContent, 10) || 0;
          if (qty <= 0) return;
          const { variantId, price, discountEligible } = this.readCardVariant(card);
          const category = card.dataset.category;
          const discountCategory = card.dataset.discountCategory || category;
          selectedQty += qty;
          subtotal += price * qty;
          if (discountEligible) {
            totalQty += qty;
            discountableSubtotal += price * qty;
            categories.add(category);
          } else {
            fixedPriceQty += qty;
          }
          items.push({ variantId, qty, discountCategory, discountEligible });
        });

        return { items, selectedQty, totalQty, subtotal, discountableSubtotal, fixedPriceQty, categories };
      }

      resolveDiscount(totalQty, categories) {
        const hasAllCats = this.categoryCount > 0 && categories.size >= this.categoryCount;
        if (totalQty >= this.tier2Qty && (!this.tier2AllCats || hasAllCats)) return this.tier2Pct;
        if (totalQty >= this.tier1Qty) return this.tier1Pct;
        return 0;
      }

      recalculate() {
        const {
          items,
          selectedQty,
          totalQty,
          subtotal,
          discountableSubtotal,
          fixedPriceQty,
          categories,
        } = this.readSelection();
        const pct = this.resolveDiscount(totalQty, categories);
        this.updateCardPrices(pct);

        if (this.summary) {
          this.summary.hidden = false;
          this.summary.classList.toggle('byob-summary--empty', selectedQty === 0);
        }
        if (this.countEl) this.countEl.textContent = totalQty;

        // Coverage chips
        this.chips.forEach((chip) => chip.classList.toggle('on', categories.has(chip.dataset.chip)));

        // Big rate
        if (this.rateEl) {
          const rateText = pct >= this.tier2Pct && this.tier2Pct > 0
            ? `&nbsp;- ${this.strings.bestDiscount}`
            : pct > 0
              ? `&nbsp;- ${this.strings.discountActive}`
              : `&nbsp;- ${this.strings.noDiscount}`;
          this.rateEl.innerHTML = `${pct}%<small>${rateText}</small>`;
        }

        this.milestones.forEach((milestone) => {
          const tier = milestone.dataset.byobMilestone;
          const active = tier === 'tier1'
            ? totalQty >= this.tier1Qty
            : pct >= this.tier2Pct && this.tier2Pct > 0;
          milestone.classList.toggle('on', active);
        });

        // Coverage hint (in the card)
        if (this.hintEl) this.hintEl.innerHTML = this.buildHint(totalQty, categories, pct);

        // Tier label in summary
        if (this.tierEl) {
          this.tierEl.innerHTML = pct > 0
            ? this.interpolate(this.strings.discountActiveHtml, { PERCENT: pct })
            : this.strings.noDiscount;
        }

        // Progress on a single track spanning both tiers (milestone dots sit at
        // tier1/tier2 positions, so fill is always relative to tier2)
        const progress = this.tier2Qty > 0 ? Math.min(100, Math.round((totalQty / this.tier2Qty) * 100)) : 0;
        if (this.fillEl) this.fillEl.style.width = progress + '%';

        // Prices
        const fixedPriceSubtotal = subtotal - discountableSubtotal;
        const discounted = fixedPriceSubtotal + Math.round(discountableSubtotal * (1 - pct / 100));
        const savings = subtotal - discounted;
        if (this.priceEl) this.priceEl.textContent = this.formatMoney(discounted);
        if (this.oldEl) {
          if (savings > 0) {
            this.oldEl.hidden = false;
            this.oldEl.textContent = this.formatMoney(subtotal);
          } else {
            this.oldEl.hidden = true;
          }
        }
        if (this.savingsEl) {
          if (savings > 0) {
            this.savingsEl.hidden = false;
            this.savingsEl.textContent = this.interpolate(this.strings.youSave, { AMOUNT: this.formatMoney(savings) });
          } else {
            this.savingsEl.hidden = true;
          }
        }
        const fixedNote = this.querySelector('[data-byob-fixed-note]');
        if (fixedNote) fixedNote.hidden = fixedPriceQty === 0;

        // Nudge
        if (this.nudgeEl) this.nudgeEl.innerHTML = this.buildNudge(totalQty, categories, pct);

        if (this.addBtn) this.addBtn.disabled = selectedQty === 0;
        this.currentItems = items;
      }

      updateCardPrices(pct) {
        this.cards.forEach((card) => {
          const { price, compareAtPrice, discountEligible } = this.readCardVariant(card);
          const oldEl = card.querySelector('[data-byob-card-old]');
          const currentEl = card.querySelector('[data-byob-card-current]');
          const priceBox = card.querySelector('[data-byob-card-price]');
          const fixedPriceNote = card.querySelector('[data-byob-fixed-price-note]');
          if (!currentEl) return;

          const hasBundleDiscount = discountEligible && pct > 0 && price > 0;
          const hasCompareAtPrice = compareAtPrice > price;
          const currentPrice = hasBundleDiscount ? Math.round(price * (1 - pct / 100)) : price;
          const referencePrice = hasBundleDiscount ? price : hasCompareAtPrice ? compareAtPrice : 0;

          if (priceBox) {
            priceBox.dataset.basePrice = String(price);
            priceBox.dataset.compareAtPrice = String(compareAtPrice);
          }
          if (oldEl) {
            oldEl.hidden = referencePrice <= 0;
            if (referencePrice > 0) oldEl.textContent = this.formatMoney(referencePrice);
          }
          currentEl.textContent = this.formatMoney(currentPrice);
          if (fixedPriceNote) fixedPriceNote.hidden = discountEligible;
          card.classList.toggle('byob-card--discounted', hasBundleDiscount);
          card.classList.toggle('byob-card--fixed-price', !discountEligible);
        });
      }

      buildHint(totalQty, categories, pct) {
        if (totalQty === 0) return this.strings.firstItem;
        if (pct >= this.tier2Pct && this.tier2Pct > 0) return this.interpolate(this.strings.maxDiscount, { PERCENT: this.tier2Pct });
        if (totalQty >= this.tier1Qty) {
          const missing = this.missingForTier2(totalQty, categories);
          return this.interpolate(this.strings.onlyRemaining, { REMAINING: missing, PERCENT: this.tier2Pct });
        }
        const remaining = Math.max(0, this.tier1Qty - totalQty);
        return this.interpolate(this.strings.remaining, { REMAINING: remaining, PERCENT: this.tier1Percent() });
      }

      buildNudge(totalQty, categories, pct) {
        if (totalQty === 0) return this.interpolate(this.strings.initialNudge, { QUANTITY: this.tier1Qty, PERCENT: this.tier1Pct });
        if (pct >= this.tier2Pct && this.tier2Pct > 0) return this.interpolate(this.strings.maxDiscountNudge, { PERCENT: this.tier2Pct });
        if (totalQty >= this.tier1Qty) {
          const missing = this.missingForTier2(totalQty, categories);
          return this.interpolate(this.strings.activeNudge, { ACTIVE: pct, REMAINING: missing, TARGET: this.tier2Pct });
        }
        const remaining = Math.max(0, this.tier1Qty - totalQty);
        return this.interpolate(this.strings.remainingNudge, { REMAINING: remaining, PERCENT: this.tier1Percent() });
      }

      /** Human text describing what's missing to reach tier 2. */
      missingForTier2(totalQty, categories) {
        const qtyMissing = Math.max(0, this.tier2Qty - totalQty);
        if (this.tier2AllCats && categories.size < this.categoryCount) {
          const catMissing = this.categoryCount - categories.size;
          if (qtyMissing > 0) return this.interpolate(this.strings.missingItemsCategories, { QUANTITY: qtyMissing, CATEGORIES: catMissing });
          return this.interpolate(this.strings.missingCategories, { CATEGORIES: catMissing });
        }
        return this.interpolate(this.strings.missingItems, { QUANTITY: qtyMissing });
      }

      interpolate(template, values) {
        return Object.entries(values).reduce(
          (result, [key, value]) => result.replaceAll(`__${key}__`, String(value)),
          template || ''
        );
      }

      tier1Percent() {
        return this.tier1Pct;
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
        }
        return (cents / 100).toLocaleString(document.documentElement.lang || 'en', { style: 'currency', currency: 'EUR' });
      }

      onAddToCart() {
        if (!this.currentItems || this.currentItems.length === 0) return;
        this.setLoading(true);
        this.showError('');

        const body = {
          items: this.currentItems.map((i) => {
            const item = { id: i.variantId, quantity: i.qty };
            if (i.discountEligible) {
              item.properties = {
                _byob: '1',
                _byob_category: i.discountCategory || '',
              };
            }
            return item;
          }),
        };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement && this.cart.setActiveElement(document.activeElement);
        }

        fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || this.strings.addError);
              return;
            }
            if (this.cart && typeof this.cart.renderContents === 'function') {
              if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              this.cart.renderContents(response);
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch((e) => {
            console.error(e);
            this.showError(this.strings.networkError);
          })
          .finally(() => this.setLoading(false));
      }

      setLoading(state) {
        if (!this.addBtn) return;
        this.addBtn.classList.toggle('loading', state);
        if (this.spinner) this.spinner.classList.toggle('hidden', !state);
        this.addBtn.disabled = state;
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.hidden = !msg;
        this.errorEl.textContent = msg || '';
      }
    }
  );
}
